import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { XCircle } from "lucide-react";
import { ProgressSpinner } from "primereact/progressspinner";
import CustomAccordion from "app/uicomponents/microcomponents/CustomAccordion";
import { getSessionStore } from "app/utils/sessionStore";
import { makeRequest } from "app/utils/makeRequest";
import { THEME, ProposalTypes } from "app/utils/types";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import WarningHex from "app/assets/IconComponent/WarningHex";
import HexagonTick from "app/assets/IconComponent/HexagonTick";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import AdjustIcon from "app/assets/IconComponent/AdjustIcon";
import { Tooltip } from "primereact/tooltip";

const formatCurrency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

interface TrueIQAlignData {
  accountValue: number;
  interestCredits: number;
  alignValue: number;
}

interface TrueIQIndexItem {
  actual: number;
  illustrated: number;
  index: number;
}

interface TrueIQIndexData {
  align: TrueIQIndexItem;
  account: TrueIQIndexItem;
  surrender: TrueIQIndexItem;
  benefit: TrueIQIndexItem;
  premium: TrueIQIndexItem;
  financing: TrueIQIndexItem;
  premiumAdjustment?: {
    totalPremiumIndex: number;
    financedPremiumIndex: number;
  };
}

interface TrueIQStatusData {
  loanAgreement: boolean;
  exitStrategy: boolean;
  collateralTracking: boolean;
  interestManaged: boolean;
}

const gradeColor = (letter: string): string => {
  if (letter.startsWith("A")) return "#22C55E";
  if (letter.startsWith("B")) return "#3B82F6";
  if (letter.startsWith("C")) return "#EAB308";
  if (letter.startsWith("D")) return "#F97316";
  return "#EF4444";
};

const indexColor = (value: number, type?: string): string => {
  if (type === "PREMIUM") return "#1E3A8A";
  if (value >= 100) return "#22C55E";
  if (value < 100) return "#EF4444";
  return "#1E3A8A";
};

const hexBgColor = (strokeColor: string): string => {
  if (strokeColor === "#22C55E") return "#F4FCF7";
  if (strokeColor === "#EF4444") return "#FFF5F5";
  return "#F5F9FF";
};

// ── Shared helpers ─────────────────────────────────────────────────────────────

const hexPolygonPoints = (cx: number, cy: number, r: number): string =>
  Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2; // pointy-top: first vertex at top
    return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
  }).join(" ");

// ── Component info data ────────────────────────────────────────────────────────

export const TRUEIQ_COMPONENT_INFO = {
  grade: {
    title: "TrueIQ Grade",
    description:
      "A weighted composite of Align Index, Financing Index, Benefit Index, Protect Score, and Status Score translated into a letter grade (A+ to F). Weightings vary by plan type to reflect each strategy's unique priorities.",
    formula: "Σ(Index_i × weight_i) + (Status × weight_s) + (Protect × weight_p)",
    valueContext:
      "Inputs: Align Index · Financing Index · Benefit Index · Protect Score · Status Score",
    gradeScale: [
      { grade: "A+", range: "> 100" },
      { grade: "A", range: "97–100" },
      { grade: "A–", range: "94–96" },
      { grade: "B+", range: "92–93" },
      { grade: "B", range: "89–91" },
      { grade: "B–", range: "86–88" },
      { grade: "C+", range: "83–85" },
      { grade: "C", range: "79–82" },
      { grade: "C–", range: "75–78" },
      { grade: "D", range: "67–74" },
      { grade: "F", range: "< 67" },
    ],
  },
  align: {
    title: "TrueIQ Align",
    description:
      "Adds actual interest credits earned during the policy year to the account value recorded at the policy anniversary. Anchors comparisons to a known policy checkpoint and removes distortions from credits applied after the anniversary date.",
    formula: "Align Value = Account Value + Interest Credits",
    valueContext: "Inputs: Account Value (at anniversary) + Interest Credits (carrier-applied)",
  },
  index: {
    title: "TrueIQ Index",
    description:
      "Measures how each key metric tracks relative to its original illustration on a 100-point scale. A score of 100 = on track; above 100 = outperforming; below 100 = trailing projection. Not an annual return or interest rate.",
    formula: "Index = (Actual Value ÷ Illustrated Value) × 100",
    valueContext:
      "Inputs: Actual vs. Illustrated for Align, Account, Surrender, Benefit, Premium, and Financing",
    subComponents: [
      {
        id: "align",
        title: "Align Index",
        description:
          "Compares the TrueIQ Aligned Value to the illustrated account value—reflects true year-end performance after interest credits.",
        formula: "(Align Value ÷ Illustrated Align Value) × 100",
      },
      {
        id: "account",
        title: "Account Index",
        description:
          "Compares actual accumulated account value to the illustrated projection at the same plan year.",
        formula: "(Actual Account Value ÷ Illustrated Account Value) × 100",
      },
      {
        id: "surrender",
        title: "Surrender Index",
        description:
          "Compares actual net surrender value to the illustrated surrender value—a measure of policy liquidity vs. plan.",
        formula: "(Actual Surrender Value ÷ Illustrated Surrender Value) × 100",
      },
      {
        id: "benefit",
        title: "Benefit Index",
        description:
          "Compares the actual death benefit to the illustrated benefit at the same policy year.",
        formula: "(Actual Benefit ÷ Illustrated Benefit) × 100",
      },
      {
        id: "premium",
        title: "Premium Index",
        description:
          "Measures how much total premium has been paid relative to the original illustrated premium schedule.",
        formula: "(Actual Premium Paid ÷ Illustrated Premium) × 100",
      },
      {
        id: "financing",
        title: "Financing Index",
        description:
          "Evaluates the loan balance vs. illustrated schedule. Formula is inverted: above 100 = lower-than-expected loan balance (favorable); below 100 = higher-than-expected (unfavorable).",
        formula: "(Illustrated Financing ÷ Actual Financing) × 100  [inverted scale]",
      },
      {
        id: "pai",
        title: "Premium-Adjusted Index (PAI)",
        description:
          "Divides any base index by the Premium Index. Reveals whether strong results stem from genuine policy efficiency or from higher-than-projected funding levels.",
        formula: "(Base Index ÷ Premium Index) × 100",
      },
    ],
  },
  protect: {
    title: "TrueIQ Protect",
    description:
      "Evaluates whether life and disability coverage meet the required protection obligations. Weighted sum of coverage ratios, with a Living Benefits bonus. Can exceed 100 when coverage surpasses requirements and includes enhanced features.",
    formula: "Protect = Σ(min(Ratio_j, 100) × weight_j) + Bonus",
    valueContext:
      "Inputs: Life Coverage Ratio · Disability Coverage Ratio · Living Benefits Bonus (+5 pts)",
    subComponents: [
      {
        id: "life",
        title: "Life Ratio",
        description: "Percent of required death benefit coverage currently in place.",
        formula: "(Actual Life Coverage ÷ Required Life Coverage) × 100",
      },
      {
        id: "disability",
        title: "Disability Ratio",
        description: "Percent of required disability benefit coverage currently in place.",
        formula: "(Actual Disability Coverage ÷ Required Disability Coverage) × 100",
      },
      {
        id: "bonus",
        title: "Living Benefits Bonus",
        description:
          "Awards +5 points when the life insurance policy includes qualifying Living Benefits or chronic illness riders.",
        formula: "+5 points if qualifying Living Benefits are present",
      },
    ],
  },
  status: {
    title: "TrueIQ Status",
    description:
      "Measures whether the plan's structural and legal execution elements are complete. Each criterion is binary (complete = 1, incomplete = 0) and weighted by strategic importance. A score of 100 means every required element is verified and in place.",
    formula: "Status = Σ(weight_i × completion_i) × 100",
    valueContext:
      "Inputs: Loan Agreement · Exit Strategy · Collateral Tracking · Interest Management",
    subComponents: [
      {
        id: "loanAgreement",
        title: "Loan Agreement",
        description: "Executed loan document defining principal, interest, and repayment terms.",
        weight: "25%",
      },
      {
        id: "exitStrategy",
        title: "Exit Strategy",
        description:
          "A documented strategy exists for loan resolution, policy transfer, or repayment.",
        weight: "25%",
      },
      {
        id: "collateralTracking",
        title: "Collateral Tracking",
        description:
          "Collateral requirements are actively monitored and updated as policy values change.",
        weight: "25%",
      },
      {
        id: "interestManaged",
        title: "Interest Managed",
        description: "Interest is being paid, accrued, or reported per the loan agreement.",
        weight: "25%",
      },
    ],
  },
} as const;

const AccordionHeader = ({
  title,
  showInfo,
  isDark,
}: {
  title: string;
  showInfo: React.ReactNode | false;
  isDark: boolean;
}) => {
  const [hovered, setHovered] = useState(false);
  const iconRef = useRef<HTMLSpanElement>(null);
  const rect = hovered && iconRef.current ? iconRef.current.getBoundingClientRect() : null;
  const [, setVisible] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
  };
  const scheduleHide = () => {
    cancelHide();
    hideTimer.current = setTimeout(() => setVisible(false), 150);
  };

  return (
    <div className="flex items-center gap-2">
      <span
        className={`text-[13px] conthrax-font flex item-start gap-1 
        }`}
      >
        <p className="font-bold" style={{ fontWeight: 700 }}>
          TrueIQ
        </p>
        {title}
      </span>

      {title !== "Disclosure" && (
        <span
          ref={iconRef}
          className="cursor-pointer inline-flex"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <WarningHex fill={isDark ? "#93C5FD" : "#191A1C"} width="15px" height="15px" />
        </span>
      )}

      {hovered &&
        rect &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: rect.right + 8,
              top: rect.top - 8,
              zIndex: 99999,
              maxWidth: 560,
              background: isDark ? "#1E2030" : "#ffffff",
              border: `1px solid ${isDark ? "#3A3A50" : "#E5E7EB"}`,
              borderRadius: "10px",
              padding: "12px 14px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              fontSize: "13px",
              color: isDark ? "#e2e8f0" : "#ffffff",
              lineHeight: "1.5",
              backgroundColor: "#495059",
            }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
          >
            {showInfo}
          </div>,
          document.body,
        )}
    </div>
  );
};

const HexBadge = ({
  label,
  value,
  color,
  large,
}: {
  label: string;
  value: number;
  color: string;
  large: boolean;
}) => {
  // Proper pointy-top proportions: width = height * √3/2
  let h = large ? 84 : 62;
  h *= 1.5;
  const w = Math.round(h * 0.866) * 1.5;
  const cx = w / 2;
  const cy = h / 2;
  const r = h / 2 - 3;

  return (
    <div className="flex flex-col items-center">
      <p
        className="text-[14px] conthrax-font font-bold uppercase tracking-widest mb-1.5 flex gap-1"
        style={{ letterSpacing: "0.08em" }}
      >
        {label === "ALIGN" ? <TrueIqIcon fill="#191A1C" width={13} /> : ""} {label}
      </p>
      <div className="relative" style={{ width: w, height: h }}>
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="absolute inset-0 rotate-90">
          <polygon
            points={hexPolygonPoints(cx, cy, r)}
            fill={hexBgColor(color)}
            stroke={color}
            strokeWidth="2"
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ gap: 2 }}
        >
          <TrueIqIcon width={54} height={32} fill={color} />
          <span
            className="font-bold leading-none conthrax-font mt-2"
            style={{ color, fontSize: "22px" }}
          >
            {value}
          </span>
        </div>
      </div>
    </div>
  );
};

const GRADE_MESSAGES: Record<string, { title: string; description: string }> = {
  "A+": {
    title: "Exceptional Performance",
    description:
      "All plan components are outperforming their original illustrations. The plan is exceeding projections across the board.",
  },
  A: {
    title: "Outstanding Performance",
    description:
      "Plan components are performing at or very close to original projections. Minor variance with no significant concerns.",
  },
  "A-": {
    title: "Excellent Performance",
    description:
      "Plan is tracking closely to projections with only minor deviations. Overall plan health is strong.",
  },
  "B+": {
    title: "Very Good",
    description:
      "Most components are performing well with slight underperformance in one or more areas. No immediate action required.",
  },
  B: {
    title: "Good",
    description:
      "Plan is generally on track with moderate variance in some components. Continue monitoring for any developing trends.",
  },
  "B-": {
    title: "Above Average",
    description:
      "Plan performance is above average with some noticeable gaps. Consider reviewing underperforming areas.",
  },
  "C+": {
    title: "Satisfactory",
    description:
      "Plan is performing adequately but several components show meaningful deviation. A review meeting is recommended.",
  },
  C: {
    title: "Adequate",
    description:
      "Significant variances exist across multiple plan components. A comprehensive plan review should be scheduled.",
  },
  "C-": {
    title: "Fair",
    description:
      "Multiple components are underperforming materially. Proactive corrective action may be needed.",
  },
  D: {
    title: "Below Average",
    description:
      "Plan is substantially underperforming projections in most key areas. Immediate advisor attention is required.",
  },
  F: {
    title: "Critical Review Needed",
    description:
      "The plan is significantly off-track. Immediate corrective action and a comprehensive strategy review are strongly recommended.",
  },
};

const TrueIqGradeContent = ({
  isDark,
  trueIqData,
  loading,
}: {
  isDark: boolean;
  trueIqData: any | null;
  loading: boolean;
}) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const subText = isDark ? "text-gray-400" : "text-[#64748B]";

  return (
    <div className="">
      <p className={`text-[13px] leading-relaxed mb-3 ${descText}`}>
        <span className="font-semibold">TrueIQ Grade</span> is based on the performance of all key
        plan components—like account values and financed loans—each scored individually and weighted
        based on their importance. It&apos;s a fast, high-level snapshot of plan health.
      </p>
      {loading ? (
        <div className="flex justify-center py-4">
          <ProgressSpinner style={{ width: "32px", height: "32px" }} />
        </div>
      ) : trueIqData ? (
        <div className="flex items-center gap-3">
          <span
            className="text-[64px] font-bold conthrax-font leading-none tracking-tight flex-shrink-0"
            style={{ color: gradeColor(trueIqData.grade) }}
          >
            {trueIqData.grade}
          </span>
          <div className="w-px self-stretch bg-gray-300 dark:bg-gray-600 flex-shrink-0 mx-1" />
          <div className="flex flex-col gap-1 min-w-0">
            {GRADE_MESSAGES[trueIqData.grade] && (
              <>
                <p
                  className="text-[15px] font-semibold conthrax-font"
                  style={{ color: gradeColor(trueIqData.grade) }}
                >
                  {GRADE_MESSAGES[trueIqData.grade].title}
                </p>
                <p className={`text-[12px] leading-relaxed ${descText}`}>
                  {GRADE_MESSAGES[trueIqData.grade].description}
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <p className={`text-[13px] ${subText}`}>No grade data available.</p>
      )}
    </div>
  );
};

const TrueIqAlignContent = ({
  isDark,
  trueIQAlign,
  loading,
}: {
  isDark: boolean;
  trueIQAlign: TrueIQAlignData | null;
  loading: boolean;
}) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const cardBg = isDark ? "bg-[#1E1E2E]" : "bg-[#F1F5F9]";
  const labelText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const valueText = isDark ? "text-gray-100" : "text-[#0F172A]";
  const symbolText = isDark ? "text-gray-400" : "text-[#94A3B8]";
  const subText = isDark ? "text-gray-400" : "text-[#64748B]";

  return (
    <div className="">
      <p className={`text-[13px] leading-relaxed mb-3 ${descText}`}>
        <span className="font-semibold">TrueIQ Align</span> enhances review accuracy by adjusting
        account (accumulated) values to reflect actual performance at the policy anniversary. This
        alignment helps displayed values more closely match year-end illustration projections.
      </p>
      {loading ? (
        <div className="flex justify-center py-4">
          <ProgressSpinner style={{ width: "32px", height: "32px" }} />
        </div>
      ) : trueIQAlign ? (
        <div className="flex items-center gap-4 justify-around flex-wrap">
          <div className={`rounded-xl p-4 min-w-[25%] ${cardBg}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${labelText}`}>
              Account Value
            </p>
            <p className={`text-[22px] font-bold conthrax-font ${valueText}`}>
              {formatCurrency(trueIQAlign.accountValue)}
            </p>
          </div>

          <span className={`text-[36px] font-light ${symbolText}`}>+</span>

          <div className={`rounded-xl p-4 min-w-[25%] ${cardBg}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${labelText}`}>
              Interest Credits
            </p>
            <p className={`text-[22px] font-bold conthrax-font ${valueText}`}>
              {formatCurrency(trueIQAlign.interestCredits)}
            </p>
          </div>

          <span className={`text-[36px] font-light ${symbolText}`}>=</span>

          <div className={`rounded-xl p-4 min-w-[25%] ${cardBg}`}>
            <div className={`flex items-center gap-1 mb-2 ${labelText}`}>
              <TrueIqIcon width={10} fill="#6C757D" />
              <p className="text-[10px] font-semibold uppercase tracking-wider">Aligned Value</p>
            </div>
            <p className={`text-[22px] font-bold conthrax-font ${valueText}`}>
              {formatCurrency(trueIQAlign.alignValue)}
            </p>
          </div>
        </div>
      ) : (
        <p className={`text-[13px] ${subText}`}>No align data available.</p>
      )}
    </div>
  );
};

const TrueIqDisclosureContent = ({ isDark }: { isDark: boolean }) => {
  const bodyText = isDark ? "text-gray-300" : "text-[#334155]";

  return (
    <div className={`flex flex-col gap-3 text-[13px] leading-relaxed ${bodyText}`}>
      <p>
        TrueIQ is an optional, informational tool designed to support insurance and financial
        planning analysis. It is provided &quot;as is&quot; without warranty and is not intended to
        serve as a substitute for official carrier documents, in-force policy illustrations, legal
        agreements, or professional advice.
      </p>
      <p>
        All outputs generated by TrueIQ—including, but not limited to, Align values, Index scores,
        Plan Grades, Status scores, Protect metrics, and component-level analytics—are based on the
        data available or entered at the time of use. These outputs are inherently limited by data
        accuracy, user inputs, carrier reporting delays, market fluctuations, and assumptions made
        in the underlying methodologies. The resulting scores and grades are interpretive and
        indicative only; they do not represent a definitive assessment of policy performance, plan
        completeness, legal sufficiency, or financial adequacy.
      </p>
      <p>
        TrueIQ does not guarantee or warrant that any plan is properly funded, contractually
        enforceable, legally sound, compliant with applicable regulations, or appropriately
        structured for the intended planning objectives. Plan Grades, Indexes, or other scores
        should not be relied upon as a definitive measure of plan health, accuracy, sufficiency, or
        execution. No aspect of TrueIQ constitutes legal, tax, insurance, or investment advice, and
        users are expressly advised to consult with appropriate professionals prior to taking or
        recommending any action based on the platform&apos;s results.
      </p>
      <p>
        IAPP and its affiliates, employees, and partners disclaim all liability for any loss, error,
        inaccuracy, or outcome resulting from the use of TrueIQ or reliance upon its calculations.
        This includes, but is not limited to, misinterpretations, omissions, inaccurate inputs, or
        failure to act on third-party professional advice. By using TrueIQ, you acknowledge and
        accept full responsibility for the interpretation and application of its outputs and agree
        that IAPP assumes no liability for direct, indirect, incidental, consequential, or punitive
        damages arising from its use.
      </p>
    </div>
  );
};

const TrueIqIndexContent = ({
  isDark,
  trueIQIndex,
  loading,
}: {
  isDark: boolean;
  trueIQIndex: TrueIQIndexData | null;
  loading: boolean;
}) => {
  const [showIndex, setShowIndex] = useState(false);
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const subText = isDark ? "text-gray-400" : "text-[#64748B]";

  const columns = useMemo(() => {
    if (!trueIQIndex) return [];

    const totalPremiumIndex = trueIQIndex.premiumAdjustment?.totalPremiumIndex ?? 1;
    const financedPremiumIndex = trueIQIndex.premiumAdjustment?.financedPremiumIndex ?? 1;

    const adj = (base: number, isFinancing = false): number => {
      if (!showIndex) return base;
      const denom = isFinancing ? financedPremiumIndex : totalPremiumIndex;
      if (!denom) return base;
      return (base / denom) * 100;
    };

    return [
      {
        main: {
          label: "ALIGN",
          value: adj(trueIQIndex.align.index),
          color: indexColor(adj(trueIQIndex.align.index)),
        },
        sub: {
          label: "ACCOUNT",
          value: adj(trueIQIndex.account.index),
          color: indexColor(adj(trueIQIndex.account.index)),
        },
      },
      {
        main: {
          label: "SURRENDER",
          value: adj(trueIQIndex.surrender.index),
          color: indexColor(adj(trueIQIndex.surrender.index)),
        },
        sub: {
          label: "BENEFIT",
          value: adj(trueIQIndex.benefit.index),
          color: indexColor(adj(trueIQIndex.benefit.index)),
        },
      },
      {
        main: {
          label: "PREMIUM",
          value: adj(trueIQIndex.premium.index),
          color: indexColor(adj(trueIQIndex.premium.index), "PREMIUM"),
        },
        sub: {
          label: "FINANCING",
          value: adj(trueIQIndex.financing.index, true),
          color: indexColor(adj(trueIQIndex.financing.index, true)),
        },
      },
    ];
  }, [showIndex, trueIQIndex]);
  const PRIMARY = "#1E3A8A";

  return (
    <div className="">
      <p className={`text-[13px] leading-relaxed mb-3 ${descText}`}>
        <span className="font-semibold">TrueIQ Index</span> displays the ratio of actual values to
        illustrated values, helping quantify how results are tracking relative to expectations. A
        value above 100 indicates performance ahead of the original projection, while a value below
        100 reflects a shortfall. This index reflects the relationship between actual and projected
        values—it does not represent an annual return or interest credit.
      </p>

      {loading ? (
        <div className="flex justify-center py-4">
          <ProgressSpinner style={{ width: "32px", height: "32px" }} />
        </div>
      ) : trueIQIndex ? (
        <div className="flex flex-col items-center relative">
          <div className="flex absolute right-0">
            <AnimatedCheckbox
              checked={showIndex}
              onChange={() => setShowIndex((v) => !v)}
              inputId="trueiq-index"
              label=""
              brandingColor={PRIMARY}
              size={20}
              hideBorder
            />
            <span id="adjust-icon-tip" className="cursor-pointer">
              <AdjustIcon width="32" height="32" fill="#6C757D" />
            </span>
            <Tooltip target="#adjust-icon-tip" content="Adjust for premium" position="bottom" />
          </div>
          <div className="flex items-center mx-auto">
            <div className="flex items-center justify-center gap-[13%]">
              {columns.map((col) => (
                <HexBadge
                  key={col.main.label}
                  label={col.main.label}
                  value={col.main.value}
                  color={col.main.color}
                  large
                />
              ))}
            </div>
          </div>
          <div className="flex items-start gap-[13%] mt-[-3%] ml-[6%]">
            {columns.map((col) => (
              <HexBadge
                key={col.sub.label}
                label={col.sub.label}
                value={col.sub.value}
                color={col.sub.color}
                large
              />
            ))}
          </div>
        </div>
      ) : (
        <p className={`text-[13px] ${subText}`}>No index data available.</p>
      )}
    </div>
  );
};

const STATUS_ITEM_DEFINITIONS = [
  {
    key: "loanAgreement" as const,
    title: "Loan Agreement",
    description: "Executed loan document defines principal, interest, and repayment terms.",
  },
  {
    key: "exitStrategy" as const,
    title: "Exit Strategy",
    description: "A clear strategy exists for loan resolution, policy transfer, or repayment.",
  },
  {
    key: "collateralTracking" as const,
    title: "Collateral Tracking",
    description: "Collateral requirements are actively monitored and updated as values change.",
  },
  {
    key: "interestManaged" as const,
    title: "Interest Managed",
    description:
      "Interest is being paid, accrued, or reported in accordance with the loan agreement.",
  },
];

const TrueIqStatusContent = ({
  isDark,
  trueIQStatus,
  loading,
}: {
  isDark: boolean;
  trueIQStatus: TrueIQStatusData | null;
  loading: boolean;
}) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const subText = isDark ? "text-gray-400" : "text-[#64748B]";

  return (
    <div className="">
      <p className={`text-[13px] leading-relaxed mb-3 ${descText}`}>
        <span className="font-semibold">TrueIQ Status</span> reflects the structural completeness of
        a plan—such as whether legal documents are in place, coverage is properly aligned, and
        planning elements have been kept up to date. This component is especially relevant for
        non-cash value strategies, where success depends on legal and strategic execution more than
        financial performance.
      </p>

      {loading ? (
        <div className="flex justify-center py-4">
          <ProgressSpinner style={{ width: "32px", height: "32px" }} />
        </div>
      ) : trueIQStatus ? (
        <div className="flex flex-col gap-3">
          {STATUS_ITEM_DEFINITIONS.map((def) => {
            const pass = trueIQStatus[def.key];
            const passBg = isDark
              ? "bg-[#14532D]/20 border-[#22C55E]/30"
              : "bg-[#F0FDF4] border-[#BBF7D0]";
            const failBg = isDark
              ? "bg-[#7F1D1D]/20 border-[#EF4444]/30"
              : "bg-[#FFF1F2] border-[#FECACA]";
            const titleColor = isDark ? "text-gray-100" : "text-[#0F172A]";
            const descColor = isDark ? "text-gray-400" : "text-[#64748B]";

            return (
              <div
                key={def.key}
                className={`flex items-start gap-3 rounded-xl border p-3 ${pass ? passBg : failBg}`}
              >
                <div className="flex-shrink-0 mt-0.5">
                  {pass ? (
                    <HexagonTick width={22} fill="#22C55E" />
                  ) : (
                    <XCircle size={22} className="text-[#EF4444]" />
                  )}
                </div>
                <div>
                  <p className={`text-[13px] conthrax-font font-semibold ${titleColor}`}>
                    {def.title}
                  </p>
                  <p className={`text-[12px] mt-0.5 ${descColor}`}>{def.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className={`text-[13px] ${subText}`}>No status data available.</p>
      )}
    </div>
  );
};

interface TrueIQProtectItem {
  role: string;
  name: string;
  avatarUrl?: string;
  score: number;
}

const ProtectHexBadge = ({
  role,
  name,
  avatarUrl,
  score,
  color,
}: {
  role: string;
  name: string;
  avatarUrl?: string;
  score: number;
  color: string;
}) => {
  // Both hexes use the same dimensions as HexBadge large=true
  let hexH = 84;
  hexH *= 1.5; // 126
  const hexW = Math.round(hexH * 0.866) * 1.5; // ≈163.5
  const hexR = hexH / 2 - 3; // circumradius ≈ 60

  const acx = hexW / 2;
  const acy = hexH / 2;
  const ar = hexR;

  const scx = hexW / 2;
  const scy = hexH / 2;
  const sr = hexR;

  // Connector width matches the flat edge of the rotated hex (≈ circumradius)
  const connectorW = Math.round(hexR);

  return (
    <div className="flex flex-col items-center">
      {/* Avatar hex — role label + avatar both inside */}
      <div className="relative" style={{ width: hexW, height: hexH }}>
        <svg
          width={hexW}
          height={hexH}
          viewBox={`0 0 ${hexW} ${hexH}`}
          className="absolute inset-0 rotate-90"
        >
          <polygon
            points={hexPolygonPoints(acx, acy, ar)}
            fill={hexBgColor(color)}
            stroke={color}
            strokeWidth="2"
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ paddingTop: 8, gap: 10 }}
        >
          <p
            className="conthrax-font font-bold uppercase"
            style={{ fontSize: "12px", letterSpacing: "0.06em", color: "#191A1C" }}
          >
            {role}
          </p>
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            <span className="conthrax-font font-bold" style={{ fontSize: "28px", color }}>
              {name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Connector bar */}
      <div
        style={{
          width: connectorW,
          height: 22,
          backgroundColor: color,
          marginTop: -10,
          marginBottom: -10,
          zIndex: 1,
        }}
      />

      {/* Score hex */}
      <div className="relative" style={{ width: hexW, height: hexH }}>
        <svg
          width={hexW}
          height={hexH}
          viewBox={`0 0 ${hexW} ${hexH}`}
          className="absolute inset-0 rotate-90"
        >
          <polygon
            points={hexPolygonPoints(scx, scy, sr)}
            fill={hexBgColor(color)}
            stroke={color}
            strokeWidth="2"
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center"
          style={{ gap: 2 }}
        >
          <TrueIqIcon width={54} height={32} fill={color} />
          <span
            className="font-bold leading-none conthrax-font mt-2"
            style={{ color, fontSize: "22px" }}
          >
            {score}
          </span>
        </div>
      </div>
    </div>
  );
};

const TrueIqProtectContent = ({
  isDark,
  items,
  loading,
}: {
  isDark: boolean;
  items: TrueIQProtectItem[];
  loading: boolean;
}) => {
  const descText = isDark ? "text-gray-300" : "text-[#334155]";
  const subText = isDark ? "text-gray-400" : "text-[#64748B]";

  return (
    <div>
      <p className={`text-[13px] leading-relaxed mb-4 ${descText}`}>
        <span className="font-semibold">TrueIQ Protect</span> evaluates whether a plan meets its
        protection goals by comparing actual insurance coverage to what&apos;s required. Each
        coverage type is scored independently, capped at 100 to maintain balance, and may be
        enhanced with a bonus for Living Benefits.
      </p>
      {loading ? (
        <div className="flex justify-center py-4">
          <ProgressSpinner style={{ width: "32px", height: "32px" }} />
        </div>
      ) : items.length > 0 ? (
        <div className="flex items-start flex-wrap gap-10">
          {items.map((item, i) => (
            <div key={i} style={{ paddingTop: i % 2 === 1 ? 60 : 0 }}>
              <ProtectHexBadge
                role={item.role}
                name={item.name}
                avatarUrl={item.avatarUrl}
                score={item.score}
                color={indexColor(item.score)}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className={`text-[13px] ${subText}`}>No protect data available.</p>
      )}
    </div>
  );
};

function TrueIq() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const planId = getSessionStore("planInfo")?.planId;
  const isBuySell = getSessionStore("planInfo")?.planType === ProposalTypes.BuySell;
  const [selectedYear, setSelectedYear] = useState<number>(getSessionStore("ircSelectedYear") ?? 1);
  const [creditType, setCreditType] = useState<string>(
    getSessionStore("ircSelectedCreditType") ?? "preCredit",
  );
  const [trueIqData, setTrueIqData] = useState<any | null>(null);
  const [gradeLoading, setGradeLoading] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const newYear =
        detail.year !== undefined
          ? detail.year
          : getSessionStore("ircSelectedYear") ?? selectedYear;
      const newCreditType =
        detail.creditType !== undefined
          ? detail.creditType
          : getSessionStore("ircSelectedCreditType") ?? creditType;
      setSelectedYear(newYear);
      setCreditType(newCreditType);
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  useEffect(() => {
    if (!planId) return;

    setGradeLoading(true);
    makeRequest("trueIQData")
      .get(`${planId}?year=${selectedYear}&creditType=${creditType}`, true)
      .then((res: any) => {
        setTrueIqData(res?.data?.data ?? null);
      })
      .catch((err: any) => {
        console.error("Failed to fetch TrueIQ data:", err);
      })
      .finally(() => setGradeLoading(false));
  }, [planId, selectedYear, creditType]);

  const sections = [
    {
      title: "Grade",
      content: (
        <TrueIqGradeContent
          isDark={isDark}
          trueIqData={trueIqData?.trueIQGrade}
          loading={gradeLoading}
        />
      ),
      showInfo: (
        <div>
          <span className="font-semibold">TrueIQ Grade</span> transforms complex plan data into a
          clear, high-level assessment of overall plan health. It integrates multiple performance
          inputs—such as structure, funding, and coverage—into a weighted composite score tailored
          to the plan type. This score is then mapped to a familiar letter grade scale (A+ to F) to
          create instant clarity for both advisors and clients. It empowers better decisions and
          makes review meetings more focused, transparent, and actionable.
        </div>
      ),
    },
    ...(!isBuySell
      ? [
          {
            title: "Align",
            content: (
              <TrueIqAlignContent
                isDark={isDark}
                trueIQAlign={trueIqData?.trueIQAlign ?? null}
                loading={gradeLoading}
              />
            ),
            showInfo: (
              <div>
                <span className="font-bold">TrueIQ Align</span> recalibrates the account value to
                reflect actual policy performance as of the most recent policy anniversary. By
                anchoring the value to a known policy checkpoint—rather than an arbitrary
                snapshot—it allows advisors to validate performance against the original year-end
                projection. This provides a cleaner, more credible baseline for discussion and
                removes common distortions caused by interest crediting appearing after policy
                anniversary.
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Index",
      content: (
        <TrueIqIndexContent
          isDark={isDark}
          trueIQIndex={trueIqData?.trueIQIndex ?? null}
          loading={gradeLoading}
        />
      ),
      showInfo: (
        <div>
          <div>
            <span className="font-semibold">TrueIQ Index</span> quantifies how each policy metric is
            tracking relative to its original illustration. It expresses actual-to-expected
            performance as a simple index—100 means the metric is performing exactly as expected,
            while scores above or below 100 show overperformance or underperformance, respectively.
            The index removes guesswork and provides a standardized tool to evaluate performance
            trends and deviations. It is not an investment return or interest rate, but a pure
            performance comparison.
          </div>
          <div>
            <span className="font-semibold">TrueIQ Premium-Adjusted Index</span> Applies a premium
            adjustment to index scores to reflect actual funding levels. Use this view to evaluate
            whether policy performance is efficient based on how much premium has actually been
            paid.
          </div>
        </div>
      ),
    },
    ...(isBuySell
      ? [
          {
            title: "Protect",
            content: (
              <TrueIqProtectContent
                isDark={isDark}
                items={trueIqData?.trueIQProtect ?? []}
                loading={gradeLoading}
              />
            ),
            showInfo: (
              <div>
                <span className="font-semibold">TrueIQ Protect</span> measures whether both life and
                disability coverage meet the financial obligations of protection-based strategies.
                It calculates how closely each coverage type aligns with its required amount and
                awards a bonus for policies that include Living Benefits. Values may exceed 100 when
                coverage is both sufficient and enhanced, providing a comprehensive view of how well
                the plan is protected against risk.
              </div>
            ),
          },
        ]
      : []),
    {
      title: "Status",
      content: (
        <TrueIqStatusContent
          isDark={isDark}
          trueIQStatus={trueIqData?.trueIQStatus ?? null}
          loading={gradeLoading}
        />
      ),
      showInfo: (
        <div>
          <span className="font-semibold text-[#ffffff]">TrueIQ Status</span> measures whether the
          plan’s structural and legal elements are in place and up to date. Each component is scored
          on a binary yes-or-no basis and weighted to reflect its importance to the overall
          strategy. A score of 100 indicates that all required structural elements have been
          completed and verified, giving the plan a clean execution profile. This helps ensure that
          strategies are not only designed well—but properly implemented and legally aligned.
        </div>
      ),
    },
    {
      title: "Disclosure",
      content: <TrueIqDisclosureContent isDark={isDark} />,
      showInfo: false,
    },
  ];

  return (
    <div className="flex flex-col gap-4 pb-6">
      {sections.map((s) => (
        <CustomAccordion
          key={s.title}
          header={<AccordionHeader title={s.title} showInfo={s.showInfo} isDark={isDark} />}
          content={s.content}
          isPendingProposal={false}
          activeIndex={0}
          headerClassName={
            isDark
              ? "trueiq-accordion-header trueiq-accordion-header-dark"
              : "trueiq-accordion-header"
          }
        />
      ))}
    </div>
  );
}

export default TrueIq;
